import { useCallback, useEffect, useState } from 'react'

type ScrollDirection = 'left' | 'right'

export function useSeasonCarousel() {
  const [listElement, setListElement] = useState<HTMLUListElement | null>(null)
  const listRef = useCallback((node: HTMLUListElement | null) => {
    setListElement(node)
  }, [])
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const updateScrollState = useCallback(() => {
    const el = listElement
    if (!el)
      return

    setCanScrollLeft(el.scrollLeft > 8) // 8px is the gap between season cards
    setCanScrollRight((el.scrollLeft + 8) + el.clientWidth < el.scrollWidth - 1)
  }, [listElement])

  useEffect(() => {
    const el = listElement
    if (!el)
      return

    const updateListWidth = () => {
      const carousel = el.parentElement
      const firstItem = el.firstElementChild as HTMLElement | null
      if (!carousel || !firstItem || firstItem.offsetWidth <= 0)
        return

      const itemWidth = firstItem.offsetWidth
      const gap = Number.parseFloat(getComputedStyle(el).columnGap) || 0
      const step = itemWidth + gap
      const arrowButtons = carousel.querySelectorAll<HTMLElement>('.season-selection-page__arrow')
      const arrowsWidth = Array.from(arrowButtons).reduce((sum, btn) => sum + btn.offsetWidth, 0)
      const carouselGap = Number.parseFloat(getComputedStyle(carousel).gap) || 0
      const availableWidth = carousel.clientWidth - arrowsWidth - carouselGap * arrowButtons.length
      const count = Math.max(1, Math.floor((availableWidth + gap) / step))

      el.style.flex = 'none'
      el.style.width = `${count * step - gap}px`
    }

    const frameId = requestAnimationFrame(() => {
      updateScrollState()
      updateListWidth()
    })

    el.addEventListener('scroll', updateScrollState)

    const ro = new ResizeObserver(() => {
      updateScrollState()
      updateListWidth()
    })

    ro.observe(el.parentElement ?? el)

    return () => {
      cancelAnimationFrame(frameId)
      el.removeEventListener('scroll', updateScrollState)
      ro.disconnect()
    }
  }, [listElement, updateScrollState])

  const scrollBy = (direction: ScrollDirection) => {
    const el = listElement
    if (!el)
      return

    const firstItem = el.firstElementChild as HTMLElement | null
    if (!firstItem)
      return

    const gap = Number.parseFloat(getComputedStyle(el).columnGap) || 0
    const step = firstItem.offsetWidth + gap
    if (step <= 0)
      return

    el.scrollBy({ left: direction === 'left' ? -step : step, behavior: 'smooth' })
  }

  return {
    listRef,
    canScrollLeft,
    canScrollRight,
    scrollBy,
  }
}
